import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  IconButton,
  Tabs,
  Tab,
  Tooltip
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DescriptionIcon from '@mui/icons-material/Description';
import CodeIcon from '@mui/icons-material/Code';

const AmazonReportModal = ({
  open,
  onClose,
  reportHtml = '',
  reportSummary = '',
  updatedCount = 0
}) => {
  const [tabIndex, setTabIndex] = useState(0);
  const [copied, setCopied] = useState(false);

  const hasHtml = Boolean(reportHtml && reportHtml.trim());
  const hasSummary = Boolean(reportSummary && reportSummary.trim());

  const handleCopySummary = () => {
    const textToCopy = reportSummary || reportHtml;
    if (textToCopy) {
      navigator.clipboard.writeText(textToCopy);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: 24,
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle
        sx={{
          m: 0,
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          bgcolor: '#f8fafc',
          borderBottom: '1px solid #e2e8f0'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 38,
              height: 38,
              borderRadius: '50%',
              bgcolor: '#ff990018',
              color: '#ff9900'
            }}
          >
            <ShoppingBagIcon />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.1rem', color: '#1e293b' }}>
              Informe de Reconciliació d'Amazon
            </Typography>
            <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>
              Resultats del motor de classificació i assignació d'articles
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {typeof updatedCount === 'number' && (
            <Chip
              icon={<CheckCircleOutlineIcon />}
              label={`${updatedCount} actualitzat(s)`}
              color={updatedCount > 0 ? 'success' : 'default'}
              size="small"
              sx={{ fontWeight: 600 }}
            />
          )}
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{
              color: (theme) => theme.palette.grey[500]
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      {hasHtml && hasSummary && (
        <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2, bgcolor: '#ffffff' }}>
          <Tabs
            value={tabIndex}
            onChange={(e, val) => setTabIndex(val)}
            textColor="primary"
            indicatorColor="primary"
          >
            <Tab icon={<DescriptionIcon fontSize="small" />} iconPosition="start" label="Informe Visual" />
            <Tab icon={<CodeIcon fontSize="small" />} iconPosition="start" label="Text Formatat" />
          </Tabs>
        </Box>
      )}

      <DialogContent
        dividers
        sx={{
          p: 2.5,
          bgcolor: '#fafafa',
          overflowY: 'auto'
        }}
      >
        {(!hasHtml && !hasSummary) ? (
          <Box sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>
            <Typography variant="body1">
              No hi ha detalls disponibles per a aquest informe.
            </Typography>
          </Box>
        ) : (tabIndex === 0 && hasHtml) ? (
          <Box
            sx={{
              bgcolor: '#ffffff',
              p: 2,
              borderRadius: 1.5,
              border: '1px solid #e2e8f0',
              '& .amazon-reconciliation-report': {
                fontFamily: 'inherit',
                color: '#1e293b'
              },
              '& table': {
                width: '100%',
                borderCollapse: 'collapse',
                margin: '12px 0',
                fontSize: '0.875rem'
              },
              '& th, & td': {
                border: '1px solid #e2e8f0',
                padding: '8px 12px',
                textAlign: 'left'
              },
              '& th': {
                backgroundColor: '#f1f5f9',
                fontWeight: 600,
                color: '#334155'
              },
              '& tr:nth-of-type(even)': {
                backgroundColor: '#f8fafc'
              },
              '& .badge, & .badge-ok, & .badge-pendent, & .badge-validar': {
                display: 'inline-block',
                padding: '2px 8px',
                borderRadius: '12px',
                fontSize: '0.75rem',
                fontWeight: 600
              },
              '& .badge-ok': {
                backgroundColor: '#dcfce7',
                color: '#166534'
              },
              '& .badge-validar, & .badge-pendent-validar': {
                backgroundColor: '#fef3c7',
                color: '#92400e'
              },
              '& .badge-pendent': {
                backgroundColor: '#fee2e2',
                color: '#991b1b'
              }
            }}
            dangerouslySetInnerHTML={{ __html: reportHtml }}
          />
        ) : (
          <Box
            component="pre"
            sx={{
              bgcolor: '#1e293b',
              color: '#f8fafc',
              p: 2,
              borderRadius: 1.5,
              fontFamily: 'Consolas, Monaco, "Courier New", monospace',
              fontSize: '0.85rem',
              overflowX: 'auto',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              m: 0
            }}
          >
            {reportSummary || reportHtml}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.5, bgcolor: '#f8fafc', justifyContent: 'space-between' }}>
        <Box>
          {(hasSummary || hasHtml) && (
            <Tooltip title={copied ? 'Copiat!' : 'Copiar text al porta-retalls'}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<ContentCopyIcon />}
                onClick={handleCopySummary}
                color={copied ? 'success' : 'inherit'}
              >
                {copied ? 'Copiat!' : 'Copiar'}
              </Button>
            </Tooltip>
          )}
        </Box>
        <Button onClick={onClose} variant="contained" color="primary">
          Tancar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AmazonReportModal;
